import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { buildSiteJsonLd } from '../seo/site-json-ld';

const SCRIPT_ID = 'hebern-jsonld';
const DEFAULT_DESCRIPTION =
  'Hebern provides automatic encryption services that integrate into your development workflow, keeping applications fast and secure.';

interface RouteJsonLdMeta {
  title: string;
  description: string;
}

@Injectable({
  providedIn: 'root',
})
export class JsonLdService {
  private readonly baseUrl = environment.frontendUrl.replace(/\/$/, '');

  /** Matches OpenGraph route keys (segment after leading slash, or "" for home). */
  private readonly routeMeta: Record<string, RouteJsonLdMeta> = {
    '': {
      title: 'Home',
      description:
        'Hebern provides automatic encryption services that quickly integrate into your data streams.',
    },
    about: {
      title: 'About',
      description:
        'Learn about Hebern and how we help you add encryption to your data streams quickly.',
    },
    'pitch-deck': {
      title: 'Pitch Deck',
      description: "Review Hebern's pitch deck and product overview.",
    },
    signup: {
      title: 'Sign Up',
      description: 'Create your Hebern account and get started.',
    },
    welcome: {
      title: 'Welcome',
      description: 'Welcome to Hebern.',
    },
    confirmation: {
      title: 'Email Confirmation',
      description: 'Confirm your email for Hebern.',
    },
    survey: {
      title: 'Survey',
      description: 'Hebern survey.',
    },
    unsubscribe: {
      title: 'Unsubscribe',
      description: 'Manage your Hebern email preferences.',
    },
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
  ) {
    this.updateStructuredData();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.updateStructuredData());
  }

  private updateStructuredData(): void {
    this.removeExistingScript();

    const routePath = this.router.url.split('?')[0];
    const pathForMeta = routePath.replace(/^\//, '') || '';
    const firstSegment = pathForMeta.split('/')[0] ?? '';
    const meta =
      this.routeMeta[firstSegment] ??
      this.routeMeta[pathForMeta] ??
      this.fallbackMeta(pathForMeta);

    const pageUrl = `${this.baseUrl}${routePath === '' ? '/' : routePath}`;
    const websiteId = `${this.baseUrl}/#website`;
    const organizationId = `${this.baseUrl}/#organization`;

    const siteDoc = buildSiteJsonLd(this.baseUrl);
    const baseGraph = (siteDoc['@graph'] as Record<string, unknown>[]) ?? [];

    const webPage: Record<string, unknown> = {
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: `${meta.title} - Hebern`,
      description: meta.description || DEFAULT_DESCRIPTION,
      isPartOf: { '@id': websiteId },
      publisher: { '@id': organizationId },
      inLanguage: 'en-US',
    };

    const breadcrumb = this.buildBreadcrumbSchema(routePath);

    const graph = [...baseGraph, webPage, breadcrumb];

    const payload = {
      '@context': 'https://schema.org',
      '@graph': graph,
    };

    this.injectSchema(payload, SCRIPT_ID);
  }

  private fallbackMeta(pathForMeta: string): RouteJsonLdMeta {
    if (!pathForMeta) {
      return this.routeMeta[''];
    }
    const words = pathForMeta.split(/[/]/)[0]?.split('-') ?? [];
    const title = words
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return {
      title: title || 'Page',
      description: DEFAULT_DESCRIPTION,
    };
  }

  private buildBreadcrumbSchema(routePath: string): Record<string, unknown> {
    const normalized = routePath.split('?')[0];
    const pathParts = normalized.split('/').filter((p) => p !== '');

    const items: Record<string, unknown>[] = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${this.baseUrl}/`,
      },
    ];

    let currentPath = '';
    pathParts.forEach((part, index) => {
      currentPath += `/${part}`;
      const name = part
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      items.push({
        '@type': 'ListItem',
        position: index + 2,
        name,
        item: `${this.baseUrl}${currentPath}`,
      });
    });

    return {
      '@type': 'BreadcrumbList',
      '@id': `${this.baseUrl}${normalized || '/'}#breadcrumb`,
      itemListElement: items,
    };
  }

  private removeExistingScript(): void {
    this.document.getElementById(SCRIPT_ID)?.remove();
  }

  private injectSchema(schema: Record<string, unknown>, id: string): void {
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  public refreshStructuredData(): void {
    this.updateStructuredData();
  }

  public injectCustomSchema(schema: Record<string, unknown>, id?: string): void {
    const schemaId = id ?? `hebern-jsonld-custom-${Date.now()}`;
    this.injectSchema(schema, schemaId);
  }
}
